/** Cache tinted copies so we don't re-walk the same JSON every render. */
const cache = new WeakMap<object, Map<string, unknown>>();

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = h.length === 3
    ? h.split('').map((c) => parseInt(c + c, 16))
    : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  return [n[0]! / 255, n[1]! / 255, n[2]! / 255];
}

function isNearBlack(rgb: number[]): boolean {
  return rgb.length >= 3 && rgb[0]! < 0.02 && rgb[1]! < 0.02 && rgb[2]! < 0.02;
}

function replaceBlack(k: number[], rgb: [number, number, number]): number[] {
  if (!isNearBlack(k)) return k;
  return k.length >= 4 ? [rgb[0], rgb[1], rgb[2], k[3]!] : [rgb[0], rgb[1], rgb[2]];
}

/**
 * Recolor near-black fills/strokes in a Lottie JSON to `hex`.
 * Prefer this over CSS `filter` (sepia/hue-rotate) — Safari/WKWebView
 * shifts that chain toward purple while Chrome keeps it blue.
 */
export function tintLottie(animationData: unknown, hex: string): unknown {
  if (!animationData || typeof animationData !== 'object') return animationData;

  let byColor = cache.get(animationData as object);
  if (!byColor) {
    byColor = new Map();
    cache.set(animationData as object, byColor);
  }
  const hit = byColor.get(hex);
  if (hit) return hit;

  const rgb = parseHex(hex);
  const clone = structuredClone(animationData);

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    const obj = node as Record<string, unknown>;
    // Fill (`fl`) / Stroke (`st`) color props — leave mask mattes (e.g. red) alone.
    if ((obj.ty === 'fl' || obj.ty === 'st') && obj.c && typeof obj.c === 'object') {
      const c = obj.c as { k?: unknown };
      const k = c.k;
      if (Array.isArray(k) && typeof k[0] === 'number') {
        c.k = replaceBlack(k as number[], rgb);
      } else if (Array.isArray(k)) {
        for (const frame of k) {
          if (frame && typeof frame === 'object' && Array.isArray((frame as { s?: number[] }).s)) {
            const f = frame as { s: number[] };
            f.s = replaceBlack(f.s, rgb);
          }
        }
      }
    }
    for (const v of Object.values(obj)) walk(v);
  };

  walk(clone);
  byColor.set(hex, clone);
  return clone;
}
